import { z } from "zod";

/** Auto-recharge buys one credit tier when the available balance crosses its threshold. */
export const autoRechargeSchema = z.object({
  enabled: z.boolean(),
  pack: z.enum(["starter", "team", "scale"]),
  threshold: z.number().int().min(0),
});
export type AutoRecharge = z.infer<typeof autoRechargeSchema>;

/** An invoice summary, backed by a confirmed payment, never a card number. */
export const invoiceSchema = z.object({
  id: z.uuid(),
  paymentId: z.uuid(),
  number: z.string().min(1),
  issuedAt: z.iso.datetime(),
  credits: z.number().int().min(1),
  subtotalCents: z.number().int().min(0),
  taxCents: z.number().int().min(0),
  totalCents: z.number().int().min(0),
  currency: z.literal("USD"),
  status: z.enum(["issued", "void"]),
});
export type Invoice = z.infer<typeof invoiceSchema>;

export const invoiceListSchema = z.array(invoiceSchema);
export type InvoiceList = z.infer<typeof invoiceListSchema>;

/**
 * Calculates invoice totals from the captured charge and project tax rate.
 * When tax rate is unset, null, or zero, the invoice total equals the charge with zero tax.
 * When a rate is explicitly configured, it splits the captured charge inclusively:
 * totalCents === amountCents and subtotalCents + taxCents === totalCents.
 */
export function calculateInvoiceTotals(
  amountCents: number,
  taxRate: number | null | undefined,
): {
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
} {
  const totalCents = amountCents;
  if (!taxRate || taxRate <= 0) {
    return {
      subtotalCents: totalCents,
      taxCents: 0,
      totalCents,
    };
  }
  const subtotalCents = Math.round(totalCents / (1 + taxRate));
  const taxCents = totalCents - subtotalCents;
  return {
    subtotalCents,
    taxCents,
    totalCents,
  };
}

/**
 * `GET /api/v1/projects/:id/billing` and `PUT .../billing`: owner-only billing settings.
 * Only brand and last four are kept for the saved card; a card number never reaches the API.
 */
export const billingSettingsSchema = z.object({
  projectId: z.uuid(),
  availableBalance: z.number().int().min(0),
  reservedBalance: z.number().int().min(0),
  monthlyLimit: z.number().int().min(0),
  perRunCap: z.number().int().min(1),
  /** True only with a live payment provider. Test mode and no provider keep it false. */
  livePayments: z.boolean(),
  autoRecharge: autoRechargeSchema,
  paymentMethod: z
    .object({
      brand: z.string().min(1),
      lastFour: z.string().regex(/^[0-9]{4}$/),
      expiry: z.string().regex(/^(0[1-9]|1[0-2])\/[0-9]{4}$/),
    })
    .nullable(),
  billingEmail: z.email().nullable(),
  taxId: z.string().nullable(),
  taxRate: z.number().min(0).max(1).nullable(),
  invoices: invoiceListSchema,
});
export type BillingSettings = z.infer<typeof billingSettingsSchema>;

/** `PUT /api/v1/projects/:id/billing`: the owner may update these settings. */
export const updateBillingSettingsSchema = z.object({
  monthlyLimit: z.number().int().min(0),
  perRunCap: z.number().int().min(1),
  autoRecharge: autoRechargeSchema,
  billingEmail: z.email().nullable(),
  taxId: z.string().trim().max(80).nullable(),
  taxRate: z.number().min(0).max(1).nullable().optional(),
});
export type UpdateBillingSettings = z.infer<typeof updateBillingSettingsSchema>;
